import { getFirestore, doc, updateDoc } from "firebase/firestore"
import { useUserStore } from "@/store/user.js"
import achievementsData from "@/data/achievements.json"
import Card from "@/classes/Card.js"
import Collection from "@/classes/Collection.js"
import notifManager from "@/assets/functions/notifManager.js"

const db = getFirestore()

let cachedCards = null
let cachedCollections = null

let achievementsManager = {
	async checkForAchievements() {
		const userStore = useUserStore()
		if (!userStore.profile) return

		const profile = userStore.profile
		const stats = profile.stats || {}
		const cards = profile.cards || {}
		const codes = profile.codes || {}
		let changes = false

		// Initialize achievements map if missing
		if (!profile.achievements) {
			profile.achievements = {}
			changes = true
		}

		// Backward compatibility: migrate old achievement id to the new one.
		if (profile.achievements.elle_est_complete === true && profile.achievements.addiction_au_secret !== true) {
			profile.achievements.addiction_au_secret = true
			delete profile.achievements.elle_est_complete
			changes = true
		}

		// Fetch card definitions for collection checks (with cache)
		if (!cachedCards) {
			cachedCards = await Card.getAll()
		}
		if (!cachedCollections) {
			cachedCollections = await Collection.getAll()
		}
		const allCardsList = cachedCards

		// 1. Pre-calculate Aggregates
		let totalCards = { common: 0, silver: 0, golden: 0, foil: 0 }
		let totalSecretCards = { common: 0, silver: 0, golden: 0, foil: 0 }

		const secretCollectionIds = new Set(
			(cachedCollections || [])
				.filter((c) => (!c.isPublic || !c.isOnLeaderboard) && profile.collections && profile.collections[c.id])
				.map((c) => c.id)
		)

		Object.entries(cards).forEach(([cardId, c]) => {
			const cardDef = allCardsList.find((def) => def.id === cardId)
			if (!cardDef) return

			// Skip if collection is not owned/unlocked
			if (!profile.collections || !profile.collections[cardDef.collection]) return

			totalCards.common += c.common || 0
			totalCards.silver += c.silver || 0
			totalCards.golden += c.golden || 0
			totalCards.foil += c.foil || 0

			if (secretCollectionIds.has(cardDef.collection)) {
				totalSecretCards.common += c.common || 0
				totalSecretCards.silver += c.silver || 0
				totalSecretCards.golden += c.golden || 0
				totalSecretCards.foil += c.foil || 0
			}
		})

		const totalCodes = (typeof stats.codeUsed === 'number') ? stats.codeUsed : Object.keys(codes).length
		const totalTrades = stats.trades || 0
		const maxTradeValue = stats.maxTradeValue || 0
		const hasStaffAccess = Array.isArray(profile.permissions) && profile.permissions.some((p) => ['dev'].includes((p || '').toLowerCase()))

		// Calculate unopened boosters
		let unopenedBoosters = 0
		if (profile.boosters) {
			unopenedBoosters = Object.values(profile.boosters).reduce((sum, count) => sum + count, 0)
		}

		// Check for secret collections
		let hasSecretCollection = false
		if (cachedCollections) {
			hasSecretCollection = secretCollectionIds.size > 0
		}

		// Group definitions by collection
		const collectionDefs = {}
		allCardsList.forEach((c) => {
			if (!collectionDefs[c.collection]) collectionDefs[c.collection] = []
			collectionDefs[c.collection].push(c.id)
		})

		// Check completeness
		let builtCollections = { common: 0, silver: 0, golden: 0, foil: 0, all: 0 }

		for (const [colName, cardIds] of Object.entries(collectionDefs)) {
			// Skip if collection is not owned/unlocked
			if (!profile.collections || !profile.collections[colName]) continue

			let isCommon = true
			let isSilver = true
			let isGolden = true
			let isFoil = true
			let isAll = true // Master completion

			if (cardIds.length === 0) continue

			for (const cid of cardIds) {
				const owned = cards[cid] || { common: 0, silver: 0, golden: 0, foil: 0 }
				if (owned.common <= 0) isCommon = false
				if (owned.silver <= 0) isSilver = false
				if (owned.golden <= 0) isGolden = false
				if (owned.foil <= 0) isFoil = false

				if (owned.common <= 0 || owned.silver <= 0 || owned.golden <= 0 || owned.foil <= 0) isAll = false
			}

			if (isCommon) builtCollections.common++
			if (isSilver) builtCollections.silver++
			if (isGolden) builtCollections.golden++
			if (isFoil) builtCollections.foil++
			if (isAll) builtCollections.all++
		}

		// 2. Iterate Achievements
		achievementsData.forEach((ach) => {
			if (profile.achievements[ach.id]) return // Already unlocked

			let unlocked = false

			switch (ach.id) {
				// Boosters
				case "collectionneur_debutant":
					unlocked = (stats.open || 0) >= 1
					break
				case "collectionneur_averti":
					unlocked = (stats.open || 0) >= 10
					break
				case "collectionneur_expert":
					unlocked = (stats.open || 0) >= 50
					break
				case "collectionneur_legendaire":
					unlocked = (stats.open || 0) >= 100
					break
				case "collectionneur_mythique":
					unlocked = (stats.open || 0) >= 500
					break

				// Cards Count
				case "je_veux_une_carte_secrete":
					unlocked = (totalSecretCards.common + totalSecretCards.silver + totalSecretCards.golden + totalSecretCards.foil) >= 1
					break
				case "je_veux_une_carte_classique":
					unlocked = totalCards.common >= 1
					break
				case "je_veux_une_carte_argent":
					unlocked = totalCards.silver >= 1
					break
				case "je_veux_une_carte_doree":
					unlocked = totalCards.golden >= 1
					break
				case "je_veux_une_carte_foil":
					unlocked = totalCards.foil >= 1
					break

				case "encore_une_carte_secrete":
					unlocked = (totalSecretCards.common + totalSecretCards.silver + totalSecretCards.golden + totalSecretCards.foil) >= 10
					break
				case "encore_une_carte_classique":
					unlocked = totalCards.common >= 10
					break
				case "encore_une_carte_argent":
					unlocked = totalCards.silver >= 10
					break
				case "encore_une_carte_doree":
					unlocked = totalCards.golden >= 10
					break
				case "encore_une_carte_foil":
					unlocked = totalCards.foil >= 10
					break

				case "toujours_plus_de_cartes_secretes":
					unlocked = (totalSecretCards.common + totalSecretCards.silver + totalSecretCards.golden + totalSecretCards.foil) >= 50
					break
				case "toujours_plus_de_cartes_classiques":
					unlocked = totalCards.common >= 50
					break
				case "toujours_plus_de_cartes_argent":
					unlocked = totalCards.silver >= 50
					break
				case "toujours_plus_de_cartes_dorees":
					unlocked = totalCards.golden >= 50
					break
				case "toujours_plus_de_cartes_foil":
					unlocked = totalCards.foil >= 50
					break

				// Codes
				case "un_petit_cadeau":
					unlocked = totalCodes >= 1
					break
				case "cadeau_utile":
					unlocked = totalCodes >= 3
					break
				case "cadeau_genereux":
					unlocked = totalCodes >= 5
					break
				case "cadeau_luxueux":
					unlocked = totalCodes >= 10
					break
				case "cadeau_des_dieux":
					unlocked = totalCodes >= 25
					break

				// Sacrifice
				case "croc_mort":
					unlocked = (stats.destroy || 0) >= 1
					break
				case "gardien_de_la_crypte":
					unlocked = (stats.destroy || 0) >= 10
					break
				case "maitre_necromancien":
					unlocked = (stats.destroy || 0) >= 50
					break
				case "seigneur_des_morts":
					unlocked = (stats.destroy || 0) >= 100
					break
				case "la_grande_faucheuse":
					unlocked = (stats.destroy || 0) >= 250
					break

				// Upgrade
				case "apprenti_forgeron":
					unlocked = (stats.upgrades || 0) >= 1
					break
				case "maitre_forgeron":
					unlocked = (stats.upgrades || 0) >= 10
					break
				case "grand_armurier":
					unlocked = (stats.upgrades || 0) >= 50
					break
				case "dieu_de_la_forge":
					unlocked = (stats.upgrades || 0) >= 100
					break
				case "forgeron_mythique":
					unlocked = (stats.upgrades || 0) >= 250
					break

				// Downgrade
				case "la_poubelle_jaune":
					unlocked = (stats.downgrades || 0) >= 1
					break
				case "station_d_epuration":
					unlocked = (stats.downgrades || 0) >= 10
					break
				case "usine_de_gestion_des_dechets":
					unlocked = (stats.downgrades || 0) >= 50
					break
				case "dieu_du_recyclage":
					unlocked = (stats.downgrades || 0) >= 100
					break
				case "recycleur_mythique":
					unlocked = (stats.downgrades || 0) >= 250
					break

				// Souls
				case "extracteur_junior":
					unlocked = (profile.souls || 0) >= 1
					break
				case "expert_en_extractions":
					unlocked = (profile.souls || 0) >= 10
					break
				case "buveur_d_ames":
					unlocked = (profile.souls || 0) >= 50
					break
				case "maitre_des_ames":
					unlocked = (profile.souls || 0) >= 100
					break
				case "dieu_des_ames":
					unlocked = (profile.souls || 0) >= 500
					break

				// Money
				case "l_argent_ne_fait_pas_le_bonheur":
					unlocked = (profile.cash || 0) >= 1000
					break
				case "je_suis_riche":
					unlocked = (profile.cash || 0) >= 5000
					break
				case "je_nage_dans_les_card_coins":
					unlocked = (profile.cash || 0) >= 10000
					break
				case "l_evasions_fiscale_ca_me_connait":
					unlocked = (profile.cash || 0) >= 25000
					break
				case "je_possede_un_pib":
					unlocked = (profile.cash || 0) >= 50000
					break

				// Collections
				case "addiction_aux_classiques":
					unlocked = builtCollections.common >= 1
					break
				case "addiction_a_l_argent":
					unlocked = builtCollections.silver >= 1
					break
				case "addiction_a_l_or":
					unlocked = builtCollections.golden >= 1
					break
				case "addiction_aux_foil":
					unlocked = builtCollections.foil >= 1
					break

				case "chasseur_de_tresors":
					unlocked = builtCollections.all >= 1
					break
				case "addiction_au_secret":
					unlocked = builtCollections.all >= 1
					break

				case "adjuge_vendu":
					unlocked = (stats.auctionBids || 0) >= 1
					break

				case "premier_echange":
					unlocked = totalTrades >= 1
					break

				case "echange_regulier":
					unlocked = totalTrades >= 10
					break

				case "negociateur_500":
					unlocked = maxTradeValue > 500
					break

				case "negociateur_1500":
					unlocked = maxTradeValue > 1500
					break
				case "negociateur_5000":
					unlocked = maxTradeValue > 5000
					break

				case "sous_blister":
					unlocked = unopenedBoosters >= 50
					break

				case "collection_secrete":
					unlocked = hasSecretCollection
					break

				case "partenaire_particulier":
					unlocked = hasStaffAccess
					break

				case "j_ai_le_bras_long":
					unlocked = hasStaffAccess
					break

				case "rencontrer_le_createur":
					unlocked = false
					break

				default:
					break
			}

			if (unlocked) {
				profile.achievements[ach.id] = true
				notifManager.sendAchievementNotif(profile.id, ach.id, `Vous avez obtenues le succès "${ach.title}" !`)
				changes = true
			}
		})

		if (changes) {
			await updateDoc(doc(db, 'profiles', profile.id), { achievements: profile.achievements })
		}
		return true
	},
}

export default achievementsManager
